import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  input,
  Input,
  Output,
  signal,
  viewChild,
  ViewChild,
  OnInit,
  WritableSignal,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isUndefined, set } from 'lodash';
import { NgScrollbar } from 'ngx-scrollbar';
import { Table } from 'primeng/table';

export interface Column {
  field?: string;
  header: string;
  templateType?: string;
  class?: string;
  minWidth?: number;
  maxWidth?: number;
  width?: number;
  frozenRight?: boolean;
  frozenLeft?: boolean;
  sortable?: boolean;
  displayCondition?: () => boolean;
  headerIcon?: any;
}

type sortingDataAccessor = (item: any, property: string) => any;

@Component({
  selector: 'u-table',
  templateUrl: './u-table.component.html',
  styleUrls: ['./u-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UTableComponent implements AfterViewInit, OnInit {
  @Input() cols: Column[];
  @Input() nodeTemplate: any;
  @Input() nodeTemplates: any;
  @Input() headerTemplate: any;
  @Input() styles: any = {};
  @Input() emptyMessage = 'No results';
  @Input() loading: boolean;
  @Input() scrollHeight = '400px';
  @Input() virtualScroll = false;
  @Input() virtualScrollItemSize: number;
  @Input() defaultSortBy: string;
  @Input() defaultSortByDirection: 'asc' | 'desc';
  @Input() set searchKeyword(val: string) {
    if (isUndefined(val)) {
      return;
    }
    this.table.filterGlobal(val, 'contains');
  }
  selectedIndex = input<number>();
  data = input<any[]>();
  sortFunction = input<sortingDataAccessor>();
  interactiveTable = input<boolean>();
  tableHeight = input<string>('100%');
  showStripes = input<boolean>(true);
  emptyStateMessage = input<string>('');
  displayEmptyState = input<boolean>(false);
  headerHeight = input<string>('40px');
  rowHeight = input<string>('56px');
  scrollToSelected = input<boolean>();
  displayedColumns;
  firstInitial = true;
  dataSource: WritableSignal<MatTableDataSource<any>> = signal(null);
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('ptable') table: Table;
  @ViewChild('scrollbarRef') scrollbarRef: NgScrollbar;
  @ViewChild('scrollbarRefHorizontal') scrollbarRefHorizontal: NgScrollbar;

  private scrollbar = viewChild<NgScrollbar>('scrollbarRef');
  useHorizontalScroll: WritableSignal<boolean> = signal(false);
  @Output() changeSelected = new EventEmitter<{ row: any; index: number }>();

  get hasClickListener(): boolean {
    return this.changeSelected && this.changeSelected.observers.length > 0;
  }

  trackById(index: number, item: any): number {
    return item.id;
  }
  constructor() {
    effect(
      () => {
        const data = this.data() || [];
        this.initDataSource(data);
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      if (this.selectedIndex() !== null && this.scrollToSelected()) {
        this.scrollToIndex();
      }
    });
  }

  ngOnInit() {
    this.displayedColumns = this.cols.filter((col) => !col.displayCondition || col.displayCondition()).map((col) => col.field);
  }

  ngAfterViewInit(): void {
    if (this.table?.scroller) {
      this.table.scroller.scrollHeight = this.scrollHeight;
    }
  }

  initDataSource(data) {
    const dataSource = new MatTableDataSource(data);
    if (this.sort) {
      dataSource.sort = this.sort;
      if (this.defaultSortBy) {
        dataSource.sort.active = this.defaultSortBy;
        dataSource.sort.direction = this.defaultSortByDirection;
      }
    }
    dataSource.sortingDataAccessor = (item, property) => {
      if (this.sortFunction()) {
        return this.sortFunction()(item, property);
      }
      return item[property];
    };
    this.dataSource.set(dataSource);
    setTimeout(() => {
      this.scrollbarRef?.update();
    }, 0);
  }

  updateScrollBar() {
    this.useHorizontalScroll.set(this.scrollbar()?.state.horizontalUsed);
  }

  setSelectedIndex(event, index, row) {
    event.stopPropagation();
    this.changeSelected.emit({ index, row });
  }

  scrollToIndex() {
    setTimeout(() => {
      const element = document.querySelector(`.table-index-${this.selectedIndex()}`);

      if (element && !this.isInViewport(element as HTMLElement)) {
        const prevElement = document.querySelector(`.table-index-${this.selectedIndex() - 1}`);
        this.scrollbarRef?.scrollToElement(prevElement as HTMLElement, { duration: 0 });
      }
    }, 0);
  }

  isInViewport(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    return rect.top >= 0 && rect.bottom <= window.innerHeight;
  }
}
